import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Server, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const features = [
  {
    icon: Server,
    title: 'RESTful Endpoints',
    description: 'Access quotes through our intuitive REST API with comprehensive documentation.'
  }
];

export default function ApiLandingPage() {
  const navigate = useNavigate();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container-praise py-8">
        {/* Header with Back Button and Logo */}
        <div className="relative mb-12">
          <div className="flex flex-col items-center">
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/')}
              className="self-start mb-8 btn-secondary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <img 
                src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/mgqlogo2.png"
                alt="MyGoodQuotes Logo"
                className="h-20 w-auto"
              />
            </motion.div>
          </div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto text-center"
        >
          {/* API Badge */}
          <motion.div variants={itemVariants} className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 rounded-full text-brand-600 font-medium">
              <Code2 className="h-4 w-4" />
              <span>API Access</span>
            </div>
          </motion.div>

          {/* Hero Content */}
          <motion.div variants={itemVariants} className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-gray-900 mb-6">
              Integrate Quotes Into Your Application
            </h1>
            <p className="text-xl text-gray-600">
              Access our powerful API to seamlessly integrate quotes into your applications.
              <br className="hidden sm:block" />
              Available upon request for verified partners.
            </p>
          </motion.div>

          {/* Feature Grid */}
          <motion.div 
            variants={containerVariants}
            className="grid md:grid-cols-1 gap-8 mb-12 max-w-xl mx-auto"
          >
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="card p-6"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-brand-50 text-brand-600 mb-4">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Contact Info */}
          <motion.div 
            variants={itemVariants}
            className="text-center"
          >
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-4">
              Interested in API Access?
            </h2>
            <p className="text-gray-600 mb-2">
              Contact our team to discuss integration possibilities and receive API documentation.
            </p>
            <p className="text-brand-600 font-medium">
              admin@mygoodquotes.com
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}